/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { Badge, EmptyState, Search, Tabs } from '@enamelai/design-system'
import { Icon as EditIcon } from '@enamelai/design-system/icons/Edit'
import { Icon as DeleteIcon } from '@enamelai/design-system/icons/Delete2'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useTheme } from '@context/ThemeContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import './BundledFavoriteStyle.css'
import {
  addBundledProcedure,
  addCommonProcedure,
  checkBundledCodeIfExists,
  deleteBundledProcedure,
  deleteCommonProcedure,
  getBundledProcedures,
  getCommonProc,
  updateBundledProcedure,
} from '@services/api/procedure.api'
import { useAppSelector } from '@store/hooks'
import AddEditBundled from './AddEditBundled.component'
import AddEditFavorite from './AddEditFavorite.component'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import { useSettingsTabSkeleton } from '@pages/Settings/useSettingsTabSkeleton'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import SettingsDropdownButton from '../../shared/SettingsDropdownButton.component'
import { logger } from '@utils/safeLogger'

const PAGE_CONTAINER = 'bundled-favorite-settings-page'

type TabId = 'bundled' | 'favorite'

const TAB_ITEMS: { id: TabId; label: string }[] = [
  { id: 'bundled', label: 'Bundles' },
  { id: 'favorite', label: 'Favorites' },
]

const formatCreatedOn = (raw?: string): string | null => {
  if (!raw) return null
  const parsed = moment(raw)
  return parsed.isValid() ? `Created on ${parsed.format('MM/DD/YYYY')}` : null
}

type ProcedureCardProps = {
  title: string
  subtitle?: string
  codes: string[]
  createdAt?: string
  badgeMode: 'solid' | 'tinted'
  onEdit: () => void
  onDelete: () => void
}

const ProcedureCard: React.FC<ProcedureCardProps> = ({
  title,
  subtitle,
  codes,
  createdAt,
  badgeMode,
  onEdit,
  onDelete,
}) => {
  const createdOn = formatCreatedOn(createdAt)
  return (
    <article className="bf-card">
      <header className="bf-card__header">
        <p className="bf-card__title">
          <strong>{title}</strong>
          {subtitle ? ` (${subtitle})` : null}
        </p>
        <div className="bf-card__actions">
          <button
            type="button"
            className="bf-card__icon bf-card__icon--edit"
            aria-label={`Edit ${title}`}
            title="Edit"
            onClick={onEdit}
          >
            <EditIcon width={16} height={16} aria-hidden />
          </button>
          <button
            type="button"
            className="bf-card__icon bf-card__icon--delete"
            aria-label={`Delete ${title}`}
            title="Delete"
            onClick={onDelete}
          >
            <DeleteIcon width={16} height={16} aria-hidden />
          </button>
        </div>
      </header>
      {codes.length > 0 && (
        <div className="bf-card__codes">
          {codes.map((code) => (
            <Badge key={code} mode={badgeMode} color="blue" label={code} />
          ))}
        </div>
      )}
      {createdOn && <p className="bf-card__date">{createdOn}</p>}
    </article>
  )
}

const BundledFavorite: React.FC = () => {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const [activeTab, setActiveTab] = useState<TabId>('bundled')
  const isTabSkeleton = useSettingsTabSkeleton(activeTab)
  const [searchQuery, setSearchQuery] = useState('')
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setHeaderButtons } = useSettingsHeader()
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const [favoriteProcedures, setFavoriteProcedures] = useState<any[]>([])
  const [bundledProcedures, setBundledProcedures] = useState<any[]>([])
  const [showAddEditBundledModal, setShowAddEditBundledModal] = useState(false)
  const [showAddEditFavoriteModal, setShowAddEditFavoriteModal] =
    useState(false)
  const [bundledToEdit, setBundledToEdit] = useState<any | null>(null)
  const [showConfirmDeleteBundledModal, setShowConfirmDeleteBundledModal] =
    useState(false)
  const [showConfirmDeleteFavoriteModal, setShowConfirmDeleteFavoriteModal] =
    useState(false)
  const [bundledToDelete, setBundledToDelete] = useState<any | null>(null)
  const [favoriteToDelete, setFavoriteToDelete] = useState<any | null>(null)
  const [listLoading, setListLoading] = useState(true)

  useSettingsSubpageLoading(listLoading)

  const fetchData = useCallback(async () => {
    try {
      const commonProceduresResponse = await getCommonProc()
      if (commonProceduresResponse.statusCode === 200) {
        setFavoriteProcedures(commonProceduresResponse.data?.common_procedures)
      } else {
        setFavoriteProcedures([])
      }
      const bundledProceduresResponse = await getBundledProcedures()
      if (bundledProceduresResponse.statusCode === 200) {
        setBundledProcedures(bundledProceduresResponse.data?.bundled_procedures)
      } else {
        setBundledProcedures([])
      }
    } catch (error) {
      console.error(error)
    } finally {
      setListLoading(false)
    }
  }, [selectedClinic])

  const resetFormData = useCallback((): void => {
    setBundledToDelete(null)
    setBundledToEdit(null)
    setFavoriteToDelete(null)
    setShowAddEditBundledModal(false)
    setShowAddEditFavoriteModal(false)
    setShowConfirmDeleteBundledModal(false)
    setShowConfirmDeleteFavoriteModal(false)
  }, [])

  const gotoListPage = useCallback((): void => {
    resetFormData()
    fetchData()
  }, [resetFormData, fetchData])

  useEffect(() => {
    gotoListPage()
  }, [gotoListPage, selectedClinic])

  const handleDeleteBundledClick = (item: any): void => {
    setBundledToDelete(item)
    setShowConfirmDeleteBundledModal(true)
  }

  const handleDeleteFavoriteClick = (item: any): void => {
    setFavoriteToDelete(item)
    setShowConfirmDeleteFavoriteModal(true)
  }

  const filteredBundledProcedures = bundledProcedures.filter(
    (bundledProcedure) => {
      const matchesSearchQuery = [
        bundledProcedure.bundled_code,
        bundledProcedure.bundled_name,
        bundledProcedure.bundled_procedure_items.map(
          (item: any) => item.procedure.procedure_code,
        ),
      ].some((field) =>
        field
          ?.toString()
          .toLowerCase()
          .includes(searchQuery?.toLowerCase() || ''),
      )

      return matchesSearchQuery
    },
  )

  const filteredFavoriteProcedures = favoriteProcedures.filter(
    (favoriteProcedure) => {
      const matchesSearchQuery = [
        favoriteProcedure.procedure.procedure_code,
        favoriteProcedure.procedure.description,
      ].some((field) =>
        field
          ?.toString()
          .toLowerCase()
          .includes(searchQuery?.toLowerCase() || ''),
      )

      return matchesSearchQuery
    },
  )

  const handleEditBundledClick = (rowData: any): void => {
    setBundledToEdit(rowData)
    setShowAddEditBundledModal(true)
  }

  const bundledActions = {
    save: async (idToEdit: string | null, formData: any): Promise<void> => {
      showLoading({
        container: PAGE_CONTAINER,
      })
      try {
        const bundledCodeExists = await checkBundledCodeIfExists(
          formData.bundled_code,
          idToEdit ?? undefined,
        )
        if (bundledCodeExists?.data?.result?.exists) {
          setTimeout(() => {
            hideLoading(PAGE_CONTAINER)
            showToast(
              createFailedToast(
                'Bundle code already exists',
                'Bundle code already exists. Please try again with a different bundle code.',
              ),
            )
          }, 2000)
          return
        }
        const response = idToEdit
          ? await updateBundledProcedure(idToEdit, formData)
          : await addBundledProcedure(formData)
        if (response.statusCode === 200) {
          setTimeout(() => {
            hideLoading(PAGE_CONTAINER)
            showToast(
              createSuccessToast(
                idToEdit
                  ? 'Bundled procedure updated successfully'
                  : 'Bundled procedure added successfully',
                idToEdit
                  ? 'Bundled procedure has been updated successfully.'
                  : 'Bundled procedure has been added successfully.',
              ),
            )
            gotoListPage()
          }, 2000)
        } else {
          setTimeout(() => {
            hideLoading(PAGE_CONTAINER)
            showToast(
              createFailedToast(
                idToEdit
                  ? 'Bundled procedure updating failed'
                  : 'Bundled procedure adding failed',
                idToEdit
                  ? 'Bundled procedure updating failed. Please try again later.'
                  : 'Bundled procedure adding failed. Please try again later.',
              ),
            )
          }, 2000)
        }
      } catch (error) {
        console.error(error)
        setTimeout(() => {
          hideLoading(PAGE_CONTAINER)
          showToast(
            createFailedToast(
              'Bundled procedure save failed',
              'Please try again later.',
            ),
          )
        }, 1000)
      }
    },
    delete: async (idToDelete: string | null): Promise<void> => {
      if (idToDelete) {
        showLoading({
          container: PAGE_CONTAINER,
        })
        try {
          const response = await deleteBundledProcedure(idToDelete)
          if (response.statusCode === 200) {
            setTimeout(() => {
              hideLoading(PAGE_CONTAINER)
              showToast(
                createSuccessToast(
                  'Bundled procedure deleted successfully',
                  'Bundled procedure has been deleted successfully.',
                ),
              )
              gotoListPage()
            }, 1000)
          } else {
            setTimeout(() => {
              hideLoading(PAGE_CONTAINER)
              showToast(
                createFailedToast(
                  'Bundled procedure not deleted',
                  'Bundled procedure not deleted. Please try again.',
                ),
              )
            }, 1000)
          }
        } catch (e) {
          logger.error('Error deleting bundled procedure:', e)
          setTimeout(() => {
            hideLoading(PAGE_CONTAINER)
            showToast(
              createFailedToast(
                'Bundled procedure not deleted',
                'Bundled procedure not deleted. Please try again.',
              ),
            )
          }, 1000)
        } finally {
          resetFormData()
        }
      }
    },
  }

  const favoriteActions = {
    saveMany: async (toAdd: string[], toRemove: string[]): Promise<void> => {
      showLoading({
        container: PAGE_CONTAINER,
      })
      try {
        const responses = await Promise.all([
          ...toAdd.map((procedure_id) => addCommonProcedure({ procedure_id })),
          ...toRemove.map((id) => deleteCommonProcedure(id)),
        ])
        const allSucceeded = responses.every((res) => res?.statusCode === 200)
        setTimeout(() => {
          hideLoading(PAGE_CONTAINER)
          showToast(
            allSucceeded
              ? createSuccessToast(
                  'Favorite procedures updated successfully',
                  'Favorite procedures have been updated successfully.',
                )
              : createFailedToast(
                  'Some favorites were not saved',
                  'Some favorite procedures could not be updated. Please try again.',
                ),
          )
          gotoListPage()
        }, 1000)
      } catch (error) {
        logger.error('Error saving favorite procedures:', error)
        setTimeout(() => {
          hideLoading(PAGE_CONTAINER)
          showToast(
            createFailedToast(
              'Favorite procedure save failed',
              'Please try again later.',
            ),
          )
          gotoListPage()
        }, 1000)
      }
    },
    delete: async (idToDelete: string | null): Promise<void> => {
      if (idToDelete) {
        showLoading({
          container: PAGE_CONTAINER,
        })
        try {
          const response = await deleteCommonProcedure(idToDelete)
          if (response.statusCode === 200) {
            setTimeout(() => {
              hideLoading(PAGE_CONTAINER)
              showToast(
                createSuccessToast(
                  'Favorite procedure deleted successfully',
                  'Favorite procedure has been deleted successfully.',
                ),
              )
              gotoListPage()
            }, 1000)
          } else {
            setTimeout(() => {
              hideLoading(PAGE_CONTAINER)
              showToast(
                createFailedToast(
                  'Favorite procedure not deleted',
                  'Favorite procedure not deleted. Please try again.',
                ),
              )
            }, 1000)
          }
        } catch (e) {
          logger.error('Error deleting favorite procedure:', e)
          setTimeout(() => {
            hideLoading(PAGE_CONTAINER)
            showToast(
              createFailedToast(
                'Favorite procedure not deleted',
                'Favorite procedure not deleted. Please try again.',
              ),
            )
          }, 1000)
        } finally {
          resetFormData()
        }
      }
    },
  }

  useEffect(() => {
    setHeaderButtons(
      <>
        <Search
          className="bf-search"
          placeholder="Search"
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
        <SettingsDropdownButton
          label="Add bundle"
          items={[
            {
              id: 'bundle',
              label: 'Add bundle',
              onSelect: () => setShowAddEditBundledModal(true),
            },
            {
              id: 'favorite',
              label: 'Add favorite',
              onSelect: () => setShowAddEditFavoriteModal(true),
            },
          ]}
        />
      </>,
    )

    return () => setHeaderButtons(null)
  }, [searchQuery, setHeaderButtons])

  const renderBundles = (): React.ReactNode => {
    if (filteredBundledProcedures.length === 0) {
      const hasNone = bundledProcedures.length === 0
      return (
        <EmptyState
          className="bf-empty"
          iconClass="mgc mgc_box_2_fill"
          title="No bundled procedures found"
          description={
            hasNone
              ? "You don't have any bundled procedures on record yet."
              : 'No bundled procedures match your search.'
          }
          actionLabel={hasNone ? 'Add bundle' : undefined}
          onAction={
            hasNone ? () => setShowAddEditBundledModal(true) : undefined
          }
        />
      )
    }
    return (
      <div className="bf-grid">
        {filteredBundledProcedures.map((item: any) => (
          <ProcedureCard
            key={item.bundled_procedure_id}
            title={item.bundled_code}
            subtitle={item.bundled_name}
            codes={item.bundled_procedure_items.map(
              (p: any) => p.procedure.procedure_code,
            )}
            createdAt={item.date_time_created_at}
            badgeMode={badgeMode}
            onEdit={() => handleEditBundledClick(item)}
            onDelete={() => handleDeleteBundledClick(item)}
          />
        ))}
      </div>
    )
  }

  const renderFavorites = (): React.ReactNode => {
    if (filteredFavoriteProcedures.length === 0) {
      const hasNone = favoriteProcedures.length === 0
      return (
        <EmptyState
          className="bf-empty"
          iconClass="mgc mgc_star_fill"
          title="No favorite procedures found"
          description={
            hasNone
              ? "You don't have any favorite procedures on record yet."
              : 'No favorite procedures match your search.'
          }
          actionLabel={hasNone ? 'Add favorite' : undefined}
          onAction={
            hasNone ? () => setShowAddEditFavoriteModal(true) : undefined
          }
        />
      )
    }
    return (
      <div className="bf-grid">
        {filteredFavoriteProcedures.map((item: any) => (
          <ProcedureCard
            key={item.common_proc_id}
            title={item.procedure.procedure_code}
            subtitle={item.procedure.description}
            codes={[]}
            createdAt={item.date_time_created_at}
            badgeMode={badgeMode}
            onEdit={() => setShowAddEditFavoriteModal(true)}
            onDelete={() => handleDeleteFavoriteClick(item)}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="bundled-favorite-settings">
      <Tabs
        className="bf-tabs"
        items={TAB_ITEMS}
        value={activeTab}
        onChange={(id) => setActiveTab(id as TabId)}
      />
      {isTabSkeleton ? (
        <SettingsSkeleton
          variant="procedure-cards"
          showTabs={false}
          showCodes={activeTab === 'bundled'}
        />
      ) : listLoading ? null : activeTab === 'bundled' ? (
        renderBundles()
      ) : (
        renderFavorites()
      )}
      {showAddEditBundledModal && (
        <AddEditBundled
          show={showAddEditBundledModal}
          onHide={() => resetFormData()}
          actions={bundledActions}
          recordToEdit={bundledToEdit}
        />
      )}
      {showConfirmDeleteBundledModal && (
        <DeleteConfirmationModal
          show={showConfirmDeleteBundledModal}
          onHide={() => resetFormData()}
          onConfirm={() =>
            bundledActions.delete(bundledToDelete?.bundled_procedure_id ?? null)
          }
          title="Delete bundled procedure?"
          bodyText={`Are you sure you want to delete bundled procedure "${bundledToDelete?.bundled_code ?? ''}"? This action can't be undone.`}
        />
      )}
      {showAddEditFavoriteModal && (
        <AddEditFavorite
          show={showAddEditFavoriteModal}
          onHide={() => resetFormData()}
          actions={favoriteActions}
          favoriteProcedures={favoriteProcedures}
        />
      )}
      {showConfirmDeleteFavoriteModal && (
        <DeleteConfirmationModal
          show={showConfirmDeleteFavoriteModal}
          onHide={() => resetFormData()}
          onConfirm={() =>
            favoriteActions.delete(favoriteToDelete?.common_proc_id ?? null)
          }
          title="Delete favorite procedure?"
          bodyText={`Are you sure you want to delete favorite procedure "${favoriteToDelete?.procedure?.procedure_code ?? ''}"? This action can't be undone.`}
        />
      )}
    </div>
  )
}

export default BundledFavorite
